"use client";

import React, { useEffect, useRef, useState } from "react";
import { Area, AreaChart } from "recharts";
import { Activity, Users, Globe, HeartHandshake } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function useCountUp(end: number, duration: number = 2000, isVisible: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTimestamp: number | null = null;
    let animationFrameId: number;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(ease * end));
      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(step);
      }
    };
    animationFrameId = window.requestAnimationFrame(step);
    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [end, duration, isVisible]);
  return count;
}
const generateTrendData = (base: number, variance: number, points: number = 10) => {
  let current = base;
  return Array.from({
    length: points
  }).map((_, i) => {
    current += Math.random() * variance;
    return {
      month: `M${i + 1}`,
      value: Math.floor(current)
    };
  });
};
const impactData = [{
  id: "projects",
  title: "Projects Completed",
  value: 2,
  prefix: "",
  suffix: "",
  icon: Activity,
  chartColor: "var(--color-chart-2)",
  data: generateTrendData(1, 0.5)
}, {
  id: "communities",
  title: "Communities Reached",
  value: 2,
  prefix: "",
  suffix: "+",
  icon: Globe,
  chartColor: "var(--color-chart-2)",
  data: generateTrendData(1, 0.5)
}, {
  id: "children",
  title: "Children Supported",
  value: 100,
  prefix: "",
  suffix: "+",
  icon: Users,
  chartColor: "var(--color-chart-2)",
  data: generateTrendData(20, 12)
}, {
  id: "partners",
  title: "Supporters & Partners",
  value: 10,
  prefix: "",
  suffix: "+",
  icon: HeartHandshake,
  chartColor: "var(--color-chart-2)",
  data: generateTrendData(2, 1.5)
}];
function StatCard({
  stat,
  isVisible,
  index
}: {
  stat: (typeof impactData)[0];
  isVisible: boolean;
  index: number;
}) {
  const animatedValue = useCountUp(stat.value, 2500 + index * 200, isVisible);
  const Icon = stat.icon;
  const chartConfig = {
    value: {
      label: "Growth",
      color: stat.chartColor
    }
  };
  return <Card data-index={index} className="relative overflow-hidden border-border/10 bg-card/10 shadow-2xl shadow-black/20 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1 hover:shadow-secondary/20">
      <CardHeader className="relative z-10 pb-2">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground/80">
            {stat.title}
          </CardTitle>

          <div className="rounded-full bg-primary/50 p-2 backdrop-blur-md" style={{
          color: stat.chartColor
        }}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardHeader>

      <CardContent className="relative z-10 pb-8">
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-extrabold tracking-tighter text-secondary sm:text-4xl">
            {stat.prefix}
            {animatedValue.toLocaleString()}
            {stat.suffix}
          </span>
        </div>
      </CardContent>

      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-24 opacity-60">
        <ChartContainer config={chartConfig} className="h-full w-full">
          <AreaChart data={stat.data} margin={{
          top: 0,
          right: 0,
          left: 0,
          bottom: 0
        }}>
            <defs>
              <linearGradient id={`fill-${stat.id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={stat.chartColor} stopOpacity={0.8} />
                <stop offset="95%" stopColor={stat.chartColor} stopOpacity={0} />
              </linearGradient>
            </defs>

            <Area type="monotone" dataKey="value" stroke={stat.chartColor} strokeWidth={3} fill={`url(#fill-${stat.id})`} isAnimationActive={isVisible} animationDuration={2500} animationBegin={index * 200} />
          </AreaChart>
        </ChartContainer>
      </div>
    </Card>;
}
export default function OurImpactTracker() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="120448" id="our-impact-tracker" className="bg-background text-foreground ![padding:40px_0px_40px_0px]">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6">
        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          <div className="mx-auto mb-10 max-w-3xl text-center sm:mb-12 md:mb-16">
            <h2 className="mb-4 text-3xl font-bold tracking-tight text-primary sm:text-4xl md:text-5xl">
              Our Impact Snapshot
            </h2>
            <p className="text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
              As a new organisation, our journey is still growing. These figures
              reflect the early steps we have taken to support education and
              community development in Ghana.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {impactData.map((stat, index) => <StatCard key={stat.id} stat={stat} isVisible={isVisible} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}